import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  GetOEMResponse,
  GetPermissionFlagsResponse,
  InventoryManagerFetchInterface,
  InventoryManagerFetchWriteInterface
} from "@dealer-center/ddc-pages-inventory-manager-interface";

const queryKeyBase = "ddc-pages-inventory-manager";

export function useReadAPI({
  resource = "",
  resourceId = "",
  asAccount = "",
  asUser = "",
  locale = "en_US",
  extraURLParams = "",
  isMock = false,
  disabled = false
}: InventoryManagerFetchInterface) {
  return useQuery({
    queryKey: [queryKeyBase, resource, resourceId],
    queryFn: async () => {
      const rstr = resource ? `/${resource}` : "";
      const ridstr = resourceId ? `/${resourceId}` : "";
      const mockstr = isMock ? "&mock" : "";

      const res = await fetch(
        `/landing/nxlambda/inventoryManager${rstr}${ridstr}?asAccount=${asAccount}&asUser=${asUser}&locale=${locale}${mockstr}${extraURLParams}`
      );
      if (res.ok) {
        return await res.json();
      } else {
        throw new Error(res.statusText);
      }
    },
    enabled: !disabled,
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
}

export function useWriteAPI({
  resource,
  asAccount,
  asUser,
  locale,
  afterSuccess
}: InventoryManagerFetchWriteInterface) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      resourceId,
      body,
      extraURLParams = ""
    }: {
      resourceId?: string;
      body?: object;
      extraURLParams?: string;
    }) => {
      const rstr = resource ? `/${resource}` : "";
      const ridstr = resourceId ? `/${resourceId}` : "";
      const res = await fetch(
        `/landing/nxlambda/inventoryManager${rstr}${ridstr}?asAccount=${asAccount || ""}&asUser=${asUser || ""}&locale=${locale || "en_US"}&${extraURLParams}`,
        {
          method: "PUT",
          body: body ? JSON.stringify(body) : "",
          headers: { "Content-Type": "application/json" }
        }
      );
      if (res.ok) {
        return await res.json();
      } else {
        throw new Error(res.statusText);
      }
    },
    onSuccess: res => {
      if (afterSuccess) {
        if (typeof afterSuccess === "string") {
          queryClient.invalidateQueries({
            queryKey: [queryKeyBase, afterSuccess]
          });
        } else {
          afterSuccess(res);
        }
      }
    }
  });
}

export function useInventoryManagerAPIInterface(
  options: InventoryManagerFetchInterface
) {
  return useReadAPI(options);
}

export function useGetOEM({
  asAccount = "",
  asUser = "",
  disabled = false
}: {
  asAccount?: string;
  asUser?: string;
  disabled?: boolean;
}) {
  return useQuery<GetOEMResponse>({
    queryKey: [queryKeyBase, "getOEM", asAccount],
    queryFn: async () => {
      const res = await fetch(
        `/landing/nxlambda/inventoryManager/getOEM?asAccount=${asAccount}&asUser=${asUser}`
      );
      if (res.ok) {
        return (await res.json()) as GetOEMResponse;
      } else {
        throw new Error(res.statusText);
      }
    },
    enabled: !disabled,
    retry: 1,
    staleTime: 1000 * 60 * 5
  });
}

export function useGetPermissionFlags({
  asAccount = "",
  asUser = "",
  disabled = false
}: {
  asAccount?: string;
  asUser?: string;
  disabled?: boolean;
}) {
  return useQuery<GetPermissionFlagsResponse>({
    queryKey: [queryKeyBase, "getPermissionFlags", asAccount, asUser],
    queryFn: async () => {
      const res = await fetch(
        `/landing/nxlambda/inventoryManager/getPermissionFlags?asAccount=${asAccount}&asUser=${asUser}`
      );
      if (res.ok) {
        return (await res.json()) as GetPermissionFlagsResponse;
      } else {
        throw new Error(res.statusText);
      }
    },
    enabled: !disabled,
    retry: 1,
    staleTime: 1000 * 60 * 5
  });
}
